import { FilterSelectionStateChangedRequestedEvent } from '@/filters/events/FilterSelectionStateChangedRequestedEvent';
import { SortingStateChangedRequestedEvent } from '@/sorting/events/SortingStateChangedRequestedEvent';
import { EventAggregationService } from '@/platform/EventAggregationService';
import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import { injectable } from 'inversify';
import { UrlEmbeddedStatePropagation } from './UrlEmbeddedStatePropagation';
import { IEmbeddedStateFactory } from './IEmbeddedStateFactory';
import { EmbeddedStateFactory } from './EmbeddedStateFactory';
import { EmbeddedStateCommittedEvent } from './events/EmbeddedStateCommittedEvent';
import { EmbeddedStateIgnoredEvent } from './events/EmbeddedStateIgnoredEvent';
import { UrlRouteProjection } from './UrlRouteProjection';
import { Routing } from '@/router/Routing';
import { NavigationPerformedEvent } from './events/NavigationPerformedEvent';
import { PaginationStateChangedRequestedEvent } from '@/store/events/PaginationStateChangedRequestedEvent';
import { PaginationDefinition } from '@/store/PaginationDefinition';
import { IEmbeddedState } from './IEmbeddedState';

type SubscribedTo =
	| FilterSelectionStateChangedRequestedEvent
	| SortingStateChangedRequestedEvent
	| PaginationStateChangedRequestedEvent
	| EmbeddedStateCommittedEvent
	| EmbeddedStateIgnoredEvent
	| NavigationPerformedEvent;

@injectable()
export class Url implements
	ISubscriber<FilterSelectionStateChangedRequestedEvent>,
	ISubscriber<SortingStateChangedRequestedEvent>,
	ISubscriber<EmbeddedStateCommittedEvent>,
	ISubscriber<EmbeddedStateIgnoredEvent>,
	ISubscriber<NavigationPerformedEvent>,
	ISubscriber<PaginationStateChangedRequestedEvent> {
	private readonly eventAggregationService: EventAggregationService;
	private readonly embeddedStatePropagation: UrlEmbeddedStatePropagation;
	private readonly embeddedState: IEmbeddedStateFactory;
	private readonly routeProjection: UrlRouteProjection;

	private readonly routing: Routing;
	private readonly window: Window;

	private disposed = false;

	public constructor(
		eventAggregationService: EventAggregationService,
		embeddedState: EmbeddedStateFactory,
		routing: Routing,
		window: Window
	) {
		this.eventAggregationService = eventAggregationService;
		this.embeddedState = embeddedState;
		this.routing = routing;
		this.window = window;

		this.embeddedStatePropagation = new UrlEmbeddedStatePropagation(eventAggregationService, embeddedState);
		this.routeProjection = new UrlRouteProjection(routing, embeddedState, eventAggregationService);

		this.eventAggregationService.subscribeTo(FilterSelectionStateChangedRequestedEvent.EventType, this);
		this.eventAggregationService.subscribeTo(SortingStateChangedRequestedEvent.EventType, this);
		this.eventAggregationService.subscribeTo(EmbeddedStateCommittedEvent.EventType, this);
		this.eventAggregationService.subscribeTo(EmbeddedStateIgnoredEvent.EventType, this);
		this.eventAggregationService.subscribeTo(NavigationPerformedEvent.EventType, this);
		this.eventAggregationService.subscribeTo(PaginationStateChangedRequestedEvent.EventType, this);
	}

	public async notify(event: SubscribedTo): Promise<void> {
		if (this.disposed) return;

		if (FilterSelectionStateChangedRequestedEvent.is(event)) {
			await this.embeddedStatePropagation.begin(
				this.embeddedState
					.fromCommitted()
					.toMutable()
					.withFilterSelection(event.state)
					.withPage(PaginationDefinition.DEFAULT_PAGE_NUMBER)
			);
		}

		if (SortingStateChangedRequestedEvent.is(event)) {
			await this.embeddedStatePropagation.begin(
				this.embeddedState
					.fromCommitted()
					.toMutable()
					.withSortingOption(event.state)
					.withPage(PaginationDefinition.DEFAULT_PAGE_NUMBER)
			);
		}

		if (EmbeddedStateCommittedEvent.is(event) || EmbeddedStateIgnoredEvent.is(event)) {
			await this.routeProjection.project();
		}

		if (NavigationPerformedEvent.is(event)) {
			await this.embeddedStatePropagation.beginFrom(event.to, event.isInitialNavigation);
		}

		if (PaginationStateChangedRequestedEvent.is(event)) {
			await this.embeddedStatePropagation.begin(this.embeddedState.fromCommitted().toMutable().withPage(event.page));
		}
	}

	public async absoluteFor(state: IEmbeddedState): Promise<string> {
		const toNavigationTarget = await this.routing.toNavigationTarget(state);
		if (toNavigationTarget === null) {
			return this.window.location.href;
		}

		const resolve = await this.routing.resolve(toNavigationTarget);
		return `${this.window.location.origin}${resolve.href}`;
	}

	public currentOrigin(): string {
		return this.window.location.origin;
	}

	public async pathFor(state: IEmbeddedState): Promise<string> {
		const toNavigationTarget = await this.routing.toNavigationTarget(state);
		if (toNavigationTarget === null) {
			return this.window.location.pathname;
		}
		const resolve = await this.routing.resolve(toNavigationTarget);
		return resolve.path;
	}

	public dispose(): void {
		this.disposed = true;
	}
}
